/**
 * agent.ts
 *
 * Single-shot agent chat — runs a driver session without a flow.
 * Useful for ad-hoc tasks and skill invocations.
 *
 * The interactive REPL lives at @skaile/workspaces/tui (startRepl).
 */

import type { AgentEvent, AgentMessage } from "@skaile/workspaces/bridge";
import { resolveAgentDir } from "@skaile/workspaces/core";
import { createLogger } from "@skaile/workspaces/core/logging";
import pc from "picocolors";
import { bootstrapRunnerLogStore } from "./logging-bootstrap.js";
import { MarkdownStreamer } from "./markdown-stream.js";
import { type AgentSession, createAgentSession } from "./session-builder.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Options for the agent REPL session (legacy).
 *
 * @docLink packages/runner/dev-guide#agent-chat-single-shot
 */
export interface AgentReplOptions {
  /** GitAgent definition directory (SOUL.md, RULES.md, knowledge/) */
  agentDir?: string;
  /** Project working directory for the agent */
  projectDir: string;
  /** Bridge driver: 'omp' | 'claude-sdk' | 'codex' (default: 'omp') */
  driver?: string;
  /** LLM model override */
  model?: string;
  /** LLM provider override */
  provider?: string;
  /** Path to settings.json for API key resolution */
  settingsFile?: string;
  /** Called for every raw agent event */
  onEvent?: (event: AgentEvent) => void;
  /** Called for log lines */
  onLog?: (line: string) => void;
  /** Prompts directory for deployed command files */
  promptsDir?: string;
}

/**
 * Options for a single-shot agent chat session.
 *
 * @docLink packages/runner/dev-guide#agent-chat-single-shot
 */
export interface AgentChatOptions extends AgentReplOptions {
  /** The message to send */
  message: string;
  /** Optional skill ID — if provided, the skill prompt is prepended */
  skill?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract printable text from an AgentMessage content field. */
function _extractText(msg: AgentMessage | undefined): string {
  if (!msg) return "";
  if (typeof msg.content === "string") return msg.content;
  if (Array.isArray(msg.content)) {
    return msg.content
      .filter((b) => b.type === "text" && b.text)
      .map((b) => b.text!)
      .join("");
  }
  return "";
}

// ---------------------------------------------------------------------------
// Styling
// ---------------------------------------------------------------------------

const PROMPT_AGENT = `${pc.magenta(pc.bold("agent"))} ${pc.dim("▶")} `;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Send a single message to an agent and wait for the response.
 * Prints agent output to stdout, then exits.
 * @docLink packages/runner/dev-guide#agent-chat-single-shot
 */
export async function runAgentChat(opts: AgentChatOptions): Promise<void> {
  const sessionId = `chat-${Date.now().toString(36)}`;
  const { dispose: disposeLogStore } = bootstrapRunnerLogStore({
    sessionId,
    projectDir: opts.projectDir,
    onLog: opts.onLog,
  });
  const chatLog = createLogger({
    kind: "runner",
    subkind: "session",
    instance: sessionId,
  });
  // Diagnostic emission goes through chatLog. The user-facing markdown stream
  // and PROMPT_AGENT prefix continue to write directly to stdout — they are
  // agent output, not diagnostics.
  const agentSession = await buildDriver(opts);
  const driver = agentSession.driver;

  await driver.start();

  let firstChunk = true;
  const mdStream = new MarkdownStreamer();
  driver.on("agent-event", (event: AgentEvent) => {
    opts.onEvent?.(event);
    if (event.type === "message_update") {
      const delta = (event as any)._textDelta;
      if (delta) {
        if (firstChunk) {
          process.stdout.write(PROMPT_AGENT);
          firstChunk = false;
        }
        mdStream.write(delta);
      }
    }
  });

  let message = opts.message;
  if (opts.skill) {
    message = `Skill: ${opts.skill}\n\n${message}`;
  }

  try {
    await new Promise<void>((resolve, reject) => {
      const onEvent = (event: AgentEvent) => {
        if (event.type === "agent_end") {
          driver.off("agent-event", onEvent);
          mdStream.end();
          process.stdout.write("\n");
          driver.kill();
          resolve();
        } else if (event.type === "error") {
          driver.off("agent-event", onEvent);
          // Preserve `detail` (category/hint) — this event fires before prompt()'s
          // own rejection, so it's the shape the CLI sees for a classified failure.
          const err = Object.assign(new Error(String((event as any).error)), {
            detail: (event as any).detail,
          });
          chatLog.error("agent emitted error event", err);
          driver.kill();
          reject(err);
        }
      };
      driver.on("agent-event", onEvent);
      driver.prompt(message).catch((err: Error) => {
        driver.off("agent-event", onEvent);
        chatLog.error("prompt failed", err);
        driver.kill();
        reject(err);
      });
    });
  } finally {
    await agentSession.dispose();
    disposeLogStore();
  }
}

// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------

// buildDriver is a thin wrapper — all setup logic lives in createAgentSession.
// agentDir/agentName are always forwarded to the driver for native identity handling
// (omp: PI_CODING_AGENT_DIR, claude-sdk: agent option).
async function buildDriver(opts: AgentReplOptions): Promise<AgentSession> {
  const agentDir = opts.agentDir ?? resolveAgentDir(opts.projectDir);
  return createAgentSession({
    projectDir: opts.projectDir,
    agentDir,
    driver: opts.driver,
    model: opts.model,
    provider: opts.provider,
    promptsDir: opts.promptsDir,
    onLog: opts.onLog,
  });
}
